import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Typography, Tabs, Tree, Table, Tag, Button, Space, Modal, Form, 
  Input, Select, Descriptions, Timeline, Badge, Alert, Row, Col, Divider 
} from 'antd';
import {
  ApartmentOutlined,
  BankOutlined,
  ContactsOutlined,
  FileTextOutlined,
  GlobalOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  ClusterOutlined,
  StarOutlined,
  PaperClipOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import * as d3 from 'd3';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

// 类型定义
interface CustomerHeadquarters {
  id: string;
  unifiedCreditCode: string; // 统一信用码
  shortName: string; // 客商简称
  fullNameCN: string; // 中文全称
  fullNameEN: string; // 英文全称
  country: string;
  customerId: string; // 统一客户ID
  isLargeCustomer?: boolean; // 大客户标识
}

interface DepartmentCustomer {
  id: string;
  headquartersId: string;
  customerType: 'peer' | 'direct'; // 同行/直客
  department: string; // 所属一级部门
  branch: string; // 分公司归属
  isPrivate: boolean; // 是否私有客户
  businessLines: BusinessLine[];
  extensions: CustomerExtension[];
}

interface BusinessLine {
  id: string;
  code: string;
  name: string;
  department: string;
  isMain: boolean; // 是否主业务线
}

interface CustomerExtension {
  type: string;
  data: any;
}

interface GroupCustomer {
  id: string;
  name: string;
  groupCode: string;
  holdingRelation: string;
  unifiedCode: string;
}

const CustomerMasterData: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerHeadquarters | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // 示例数据
  const headquartersData: CustomerHeadquarters[] = [
    {
      id: 'hq1',
      unifiedCreditCode: '91310000631377728K',
      shortName: '中远海运',
      fullNameCN: '中国远洋海运集团有限公司',
      fullNameEN: 'China COSCO Shipping Corporation Limited',
      country: '中国',
      customerId: 'CUST-001',
      isLargeCustomer: true
    },
    {
      id: 'hq2',
      unifiedCreditCode: '91440300192181490G',
      shortName: '华为',
      fullNameCN: '华为技术有限公司',
      fullNameEN: 'Huawei Technologies Co., Ltd.',
      country: '中国',
      customerId: 'CUST-002',
      isLargeCustomer: true
    }
  ];

  const departmentCustomers: DepartmentCustomer[] = [
    {
      id: 'dc1',
      headquartersId: 'hq1',
      customerType: 'direct',
      department: '国际货运部',
      branch: '上海分公司',
      isPrivate: false,
      businessLines: [
        { id: 'bl1', code: 'SEA-FCL', name: '海运整箱', department: '国际货运部', isMain: true },
        { id: 'bl2', code: 'SEA-LCL', name: '海运拼箱', department: '国际货运部', isMain: false }
      ],
      extensions: []
    },
    {
      id: 'dc2',
      headquartersId: 'hq2',
      customerType: 'direct',
      department: '供应链管理部',
      branch: '深圳分公司',
      isPrivate: true,
      businessLines: [
        { id: 'bl3', code: 'AIR-EXP', name: '空运快件', department: '供应链管理部', isMain: true }
      ],
      extensions: []
    }
  ];

  // 扩展信息类型
  const extensionTypes = [
    { key: 'logistics_role', name: '物流角色', icon: <ApartmentOutlined /> },
    { key: 'participants', name: '参与人', icon: <TeamOutlined /> },
    { key: 'contacts', name: '客商联系人', icon: <ContactsOutlined /> },
    { key: 'bank_accounts', name: '客商银行账号', icon: <BankOutlined /> },
    { key: 'settlement', name: '客商结算对象', icon: <FileTextOutlined /> },
    { key: 'invoice_info', name: '客商开票信息', icon: <FileTextOutlined /> },
    { key: 'operation_notes', name: '操作注意事项', icon: <ExclamationCircleOutlined /> },
    { key: 'major_events', name: '重大事件', icon: <ExclamationCircleOutlined /> },
    { key: 'authorized_orgs', name: '授权机构', icon: <SafetyCertificateOutlined /> },
    { key: 'new_business', name: '新业务定义', icon: <StarOutlined /> },
    { key: 'attachments', name: '附件单据', icon: <PaperClipOutlined /> },
    { key: 'qualifications', name: '行业协会资质', icon: <SafetyCertificateOutlined /> }
  ];

  // 绘制实体关系图
  useEffect(() => {
    if (selectedTab !== 'relationship') {
      return;
    }

    // 添加延迟确保DOM元素已经渲染
    const timer = setTimeout(() => {
      if (!svgRef.current) {
        return;
      }

      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();

      const width = 900;
      const height = 600;
      
      svg.attr('width', width).attr('height', height);

    // 定义节点
    const nodes = [
      { id: 'headquarters', name: '客商总部信息', type: 'root', x: width / 2, y: 100 },
      { id: 'dept_customer', name: '客商\n(一级部门私有)', type: 'entity', x: width / 3, y: 250 },
      { id: 'business_line', name: '客户业务线\n(一级部门私有)', type: 'entity', x: width * 2 / 3, y: 250 },
      { id: 'extensions', name: '客户扩展信息', type: 'entity', x: width / 4, y: 400 },
      { id: 'group', name: '集团客户', type: 'entity', x: width * 3 / 4, y: 400 },
      { id: 'large_customer', name: '大客户标识', type: 'entity', x: width / 2, y: 400 }
    ];

    // 定义连线
    const links = [
      { source: 'headquarters', target: 'dept_customer', label: '1:N' },
      { source: 'headquarters', target: 'large_customer', label: '1:N' },
      { source: 'dept_customer', target: 'business_line', label: 'N:N' },
      { source: 'dept_customer', target: 'extensions', label: '1:N' },
      { source: 'dept_customer', target: 'group', label: '1:N' },
      { source: 'business_line', target: 'group', label: '1:N' }
    ];

    // 绘制连线
    const linkGroup = svg.append('g');
    links.forEach(link => {
      const source = nodes.find(n => n.id === link.source)!;
      const target = nodes.find(n => n.id === link.target)!;
      
      linkGroup.append('line')
        .attr('x1', source.x)
        .attr('y1', source.y)
        .attr('x2', target.x)
        .attr('y2', target.y)
        .attr('stroke', '#999')
        .attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrowhead)');
      
      // 添加关系标签
      const midX = (source.x + target.x) / 2;
      const midY = (source.y + target.y) / 2;
      linkGroup.append('text')
        .attr('x', midX)
        .attr('y', midY)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('fill', '#666')
        .attr('background', 'white')
        .text(link.label);
    });

    // 定义箭头
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 10)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#999');

    // 绘制节点
    const nodeGroup = svg.append('g');
    nodes.forEach(node => {
      const g = nodeGroup.append('g')
        .attr('transform', `translate(${node.x}, ${node.y})`);
      
      // 根据类型选择不同的形状和颜色
      if (node.type === 'root') {
        g.append('rect')
          .attr('x', -80)
          .attr('y', -30)
          .attr('width', 160)
          .attr('height', 60)
          .attr('rx', 10)
          .attr('fill', '#1890ff')
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);
      } else {
        g.append('rect')
          .attr('x', -70)
          .attr('y', -25)
          .attr('width', 140)
          .attr('height', 50)
          .attr('rx', 5)
          .attr('fill', '#f0f0f0')
          .attr('stroke', '#d9d9d9')
          .attr('stroke-width', 1);
      }
      
      // 添加文字
      const lines = node.name.split('\n');
      lines.forEach((line, i) => {
        g.append('text')
          .attr('x', 0)
          .attr('y', i * 16 - (lines.length - 1) * 8)
          .attr('text-anchor', 'middle')
          .attr('font-size', '14px')
          .attr('fill', node.type === 'root' ? 'white' : '#333')
          .text(line);
      });
    });

    }, 100); // 100ms延迟

    return () => clearTimeout(timer);
  }, [selectedTab]);

  // 渲染树形结构数据
  const treeData = headquartersData.map(hq => ({
    title: hq.shortName,
    key: hq.id,
    icon: <GlobalOutlined />,
    children: departmentCustomers
      .filter(dc => dc.headquartersId === hq.id)
      .map(dc => ({
        title: `${dc.department} - ${dc.branch}`,
        key: dc.id,
        icon: <ApartmentOutlined />,
        children: [
          {
            title: '业务线',
            key: `${dc.id}-bl`,
            icon: <ClusterOutlined />,
            children: dc.businessLines.map(bl => ({
              title: `${bl.name} ${bl.isMain ? '(主)' : ''}`,
              key: bl.id,
              isLeaf: true
            }))
          },
          {
            title: '扩展信息',
            key: `${dc.id}-ext`,
            icon: <FileTextOutlined />,
            children: extensionTypes.map(et => ({
              title: et.name,
              key: `${dc.id}-${et.key}`,
              icon: et.icon,
              isLeaf: true
            }))
          }
        ]
      }))
  }));

  // 总部信息表格列
  const headquartersColumns = [
    {
      title: '统一信用码',
      dataIndex: 'unifiedCreditCode',
      key: 'unifiedCreditCode',
      width: 200
    },
    {
      title: '客商简称',
      dataIndex: 'shortName',
      key: 'shortName',
      render: (text: string, record: CustomerHeadquarters) => (
        <Space>
          <a onClick={() => setSelectedCustomer(record)}>{text}</a>
          {record.isLargeCustomer && <Tag color="gold">大客户</Tag>}
        </Space>
      )
    },
    {
      title: '中文全称',
      dataIndex: 'fullNameCN',
      key: 'fullNameCN'
    },
    {
      title: '英文全称',
      dataIndex: 'fullNameEN',
      key: 'fullNameEN'
    },
    {
      title: '国家/地区',
      dataIndex: 'country',
      key: 'country',
      width: 100
    },
    {
      title: '统一客户ID',
      dataIndex: 'customerId',
      key: 'customerId',
      width: 120
    }
  ];

  const tabItems = [
    {
      key: 'overview',
      label: '数据总览',
      children: (
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Card>
              <Title level={4}>客商层级结构</Title>
              <Tree
                treeData={treeData}
                showIcon
                defaultExpandedKeys={['hq1']}
                style={{ maxHeight: 500, overflow: 'auto' }}
              />
            </Card>
          </Col>
          <Col span={16}>
            <Card title="客商总部信息" style={{ marginBottom: 16 }}>
              <Table
                columns={headquartersColumns}
                dataSource={headquartersData}
                rowKey="id"
                pagination={false}
              />
            </Card>
            
            <Card title="数据统计">
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Card type="inner">
                    <div style={{ textAlign: 'center' }}>
                      <GlobalOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                          {headquartersData.length}
                        </div>
                        <div>客商总部</div>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card type="inner">
                    <div style={{ textAlign: 'center' }}>
                      <ApartmentOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                          {departmentCustomers.length}
                        </div>
                        <div>部门客商</div>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card type="inner">
                    <div style={{ textAlign: 'center' }}>
                      <ClusterOutlined style={{ fontSize: 32, color: '#fa8c16' }} />
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                          {departmentCustomers.reduce((sum, dc) => sum + dc.businessLines.length, 0)}
                        </div>
                        <div>业务线</div>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card type="inner">
                    <div style={{ textAlign: 'center' }}>
                      <FileTextOutlined style={{ fontSize: 32, color: '#eb2f96' }} />
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold' }}>12</div>
                        <div>扩展信息类型</div>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )
    },
    {
      key: 'relationship',
      label: '实体关系图',
      children: (
        <>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <svg ref={svgRef}></svg>
            </div>
          </Card>
          
          <Card title="实体说明" style={{ marginTop: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Title level={5}>核心实体</Title>
                <Timeline>
                  <Timeline.Item color="blue">
                    <strong>客商总部信息</strong>：集团唯一识别，统一信用码为主键
                  </Timeline.Item>
                  <Timeline.Item color="green">
                    <strong>客商(一级部门私有)</strong>：部门级客户管理，支持私有化
                  </Timeline.Item>
                  <Timeline.Item color="orange">
                    <strong>客户业务线</strong>：细分业务管理，支持多对多关系
                  </Timeline.Item>
                </Timeline>
              </Col>
              <Col span={12}>
                <Title level={5}>扩展实体</Title>
                <Timeline>
                  <Timeline.Item>
                    <strong>客户扩展信息</strong>：12类可配置的扩展属性
                  </Timeline.Item>
                  <Timeline.Item>
                    <strong>集团客户</strong>：集团关系管理
                  </Timeline.Item>
                  <Timeline.Item>
                    <strong>大客户标识</strong>：VIP客户标记
                  </Timeline.Item>
                </Timeline>
              </Col>
            </Row>
          </Card>
        </>
      )
    },
    {
      key: 'extensions',
      label: '扩展信息管理',
      children: (
        <>
          <Row gutter={[16, 16]}>
            {extensionTypes.map(ext => (
              <Col span={6} key={ext.key}>
                <Card 
                  hoverable
                  onClick={() => setModalVisible(true)}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 32, color: '#1890ff' }}>{ext.icon}</div>
                    <div style={{ marginTop: 8, fontWeight: 'bold' }}>{ext.name}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                      点击配置
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
          
          <Card title="扩展信息说明" style={{ marginTop: 16 }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="物流角色">
                与客户相关的物流属性定义（发货人、收货人、通知方等）
              </Descriptions.Item>
              <Descriptions.Item label="参与人">
                内部或外部业务参与人员信息
              </Descriptions.Item>
              <Descriptions.Item label="客商联系人">
                常用联系人信息（姓名、邮箱、电话、职位）
              </Descriptions.Item>
              <Descriptions.Item label="客商银行账号">
                用于收付款的银行账户信息
              </Descriptions.Item>
              <Descriptions.Item label="客商结算对象">
                结算单位信息和结算规则
              </Descriptions.Item>
              <Descriptions.Item label="客商开票信息">
                纳税人识别号、开票抬头、地址电话等
              </Descriptions.Item>
              <Descriptions.Item label="操作注意事项">
                黑名单、注意条款、限制操作等风控信息
              </Descriptions.Item>
              <Descriptions.Item label="重大事件">
                客户相关历史大事件，如冻结记录、纠纷等
              </Descriptions.Item>
              <Descriptions.Item label="授权机构">
                所属的认证机构或官方背景
              </Descriptions.Item>
              <Descriptions.Item label="新业务定义">
                特殊业务场景标记和配置
              </Descriptions.Item>
              <Descriptions.Item label="附件单据">
                合同、资质、截图、发票等资料管理
              </Descriptions.Item>
              <Descriptions.Item label="行业协会资质">
                WCA、NVOCC、FIATA等行业资质信息
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )
    },
    {
      key: 'ddd',
      label: 'DDD建模分析',
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card title="聚合设计">
                <Title level={5}>客商聚合</Title>
                <ul>
                  <li><strong>聚合根</strong>：客商总部信息</li>
                  <li><strong>实体</strong>：部门客商、客户业务线、集团客户</li>
                  <li><strong>值对象</strong>：统一信用码、联系信息、银行账号</li>
                </ul>
                
                <Divider />
                
                <Title level={5}>边界上下文</Title>
                <ul>
                  <li><strong>客户管理上下文</strong>：核心客户信息维护</li>
                  <li><strong>权限上下文</strong>：部门私有数据隔离</li>
                  <li><strong>扩展信息上下文</strong>：灵活的属性配置</li>
                </ul>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="领域事件">
                <Timeline>
                  <Timeline.Item color="blue">
                    客商创建事件 - CustomerCreatedEvent
                  </Timeline.Item>
                  <Timeline.Item color="green">
                    部门分配事件 - DepartmentAssignedEvent
                  </Timeline.Item>
                  <Timeline.Item color="orange">
                    业务线变更事件 - BusinessLineChangedEvent
                  </Timeline.Item>
                  <Timeline.Item color="red">
                    客商冻结事件 - CustomerFrozenEvent
                  </Timeline.Item>
                  <Timeline.Item>
                    扩展信息更新事件 - ExtensionUpdatedEvent
                  </Timeline.Item>
                </Timeline>
              </Card>
            </Col>
          </Row>
          
          <Card title="业务规则" style={{ marginTop: 16 }}>
            <Alert
              message="核心不变量"
              description={
                <ul>
                  <li>统一信用码全局唯一，不可修改</li>
                  <li>一个客商在同一部门下只能有一条记录</li>
                  <li>主业务线有且仅有一条</li>
                  <li>私有客商数据仅本部门可见</li>
                  <li>大客户标识需要审批流程</li>
                </ul>
              }
              type="warning"
            />
          </Card>
        </>
      )
    }
  ];

  return (
    <div>
      <Title level={2}>客户主数据管理系统</Title>
      <Paragraph>
        基于集团客户管理体系的主数据模型，展示客商总部信息、部门私有客商、业务线、扩展信息等实体关系。
      </Paragraph>

      <Alert
        message="主数据治理说明"
        description={
          <div>
            <p>• <strong>唯一识别</strong>：以统一信用码作为客商总部的唯一标识</p>
            <p>• <strong>层级管理</strong>：总部 → 部门客商 → 业务线的三级结构</p>
            <p>• <strong>权限隔离</strong>：一级部门私有客商数据相互隔离</p>
            <p>• <strong>扩展灵活</strong>：支持12类扩展信息的动态配置</p>
          </div>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      <Tabs 
        activeKey={selectedTab} 
        onChange={setSelectedTab}
        items={tabItems}
      />

      {selectedCustomer && (
        <Modal
          title={`客户详情 - ${selectedCustomer.shortName}`}
          open={!!selectedCustomer}
          onCancel={() => setSelectedCustomer(null)}
          width={800}
          footer={null}
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item label="统一信用码" span={2}>
              {selectedCustomer.unifiedCreditCode}
            </Descriptions.Item>
            <Descriptions.Item label="中文全称">
              {selectedCustomer.fullNameCN}
            </Descriptions.Item>
            <Descriptions.Item label="英文全称">
              {selectedCustomer.fullNameEN}
            </Descriptions.Item>
            <Descriptions.Item label="国家/地区">
              {selectedCustomer.country}
            </Descriptions.Item>
            <Descriptions.Item label="统一客户ID">
              {selectedCustomer.customerId}
            </Descriptions.Item>
            <Descriptions.Item label="大客户标识" span={2}>
              {selectedCustomer.isLargeCustomer ? 
                <Badge status="success" text="是" /> : 
                <Badge status="default" text="否" />
              }
            </Descriptions.Item>
          </Descriptions>
        </Modal>
      )}

      <Modal
        title="配置扩展信息"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form layout="vertical">
          <Form.Item label="信息类型">
            <Select placeholder="选择扩展信息类型">
              {extensionTypes.map(ext => (
                <Option key={ext.key} value={ext.key}>
                  {ext.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="配置内容">
            <Input.TextArea rows={4} placeholder="输入配置信息" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary">保存</Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerMasterData;